import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../services/authService';
import { Pagination as PaginationType } from '../types';
import { colors } from '../styles/colors';
import Pagination from './Pagination';
import CompanySubscriptionViewModal from './CompanySubscriptionViewModal';
import CompanySubscriptionEditModal from './CompanySubscriptionEditModal';

interface CompanySubscription {
  id: number;
  company_id: number;
  active: boolean;
  activated_at: string | null;
  deactivated_at: string | null;
  canceled_at: string | null;
  trial: boolean;
  trial_ends_at: string | null;
  closing_day: number | null;
  payment_method: string | null;
  mrr_value: number;
  bill_value: number;
  interval_months: number;
  terms_attachment_file_name: string | null;
  trial_welcome_checked: boolean;
  terms_accepted: boolean;
  company: {
    id: number;
    name: string;
    fancy_name: string | null;
  } | null;
  billing: {
    id: number;
    cnpj: string | null;
    name: string | null;
    email: string | null;
    phone: string | null;
    info: string | null;
    zip: string | null;
    neighborhood: string | null;
    address1: string | null;
    number: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    simples_nacional: boolean;
  } | null;
}

const CompanySubscriptions: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<CompanySubscription[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('company_name');
  const [filter, setFilter] = useState('all');
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string>('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    fetchSubscriptions();
  }, [pagination.page, searchTerm, filter]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        filter,
        ...(searchTerm && { search: searchTerm, searchType }),
      });

      const response = await api.get(`/company-subscriptions?${params}`);
      setSubscriptions(response.data.subscriptions);
      setPagination(response.data.pagination);
    } catch (error: any) {
      toast.error('Erro ao carregar assinaturas');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(search);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchTypeChange = (newType: string) => {
    setSearchType(newType);
    setSearch('');
    setSearchTerm('');
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setSearch('');
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatClosingDay = (subscription: CompanySubscription) => {
    if (!subscription.closing_day) return '-';
    
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const today = new Date();
    
    // Se for trial, retorna trial_ends_at
    if (subscription.trial && subscription.trial_ends_at) {
      const trialEnd = new Date(subscription.trial_ends_at);
      return `${trialEnd.getDate()}/${months[trialEnd.getMonth()]}`;
    }
    
    // Para subscriptions, calcula próximo vencimento
    let closingDate = new Date(today.getFullYear(), today.getMonth(), subscription.closing_day);
    
    // Se a data já passou neste mês, pega o próximo mês
    if (closingDate <= today) {
      closingDate = new Date(today.getFullYear(), today.getMonth() + 1, subscription.closing_day);
    }
    
    return `${subscription.closing_day}/${months[closingDate.getMonth()]}`;
  };

  const getBillingStatus = (billing: CompanySubscription['billing']) => {
    if (!billing) {
      return { color: 'red', icon: '✗', text: 'dados de cobrança' };
    }
    
    const emptyCount = Object.values(billing).filter(v => v === null || v === '').length;
    
    if (emptyCount < 3) {
      return { color: 'green', icon: '✓', text: 'dados de cobrança' };
    } else {
      return { color: '#e29b20', icon: '⚠', text: 'dados de cobrança' };
    }
  };

  const calculateBillValue = (subscription: CompanySubscription) => {
    if (subscription.mrr_value === 0) {
      return '<span style="color:#bbb;">FREE</span>';
    }

    if (!subscription.billing?.state || subscription.mrr_value <= 0) {
      return '<span style="color:#bbb; white-space:nowrap;">FALTAM DADOS</span>';
    }

    const mrrValue = subscription.mrr_value;
    const csrf = subscription.billing.simples_nacional ? 0 : mrrValue * 0.0465;
    const ir = mrrValue >= 666 ? mrrValue * 0.015 : 0;
    
    let tax;
    if (subscription.billing.state.trim().toLowerCase() === 'sp') {
      tax = csrf + ir;
    } else {
      tax = mrrValue * 0.05 + csrf + ir;
    }

    return formatCurrency(mrrValue - tax);
  };

  const getSearchPlaceholder = () => {
    switch(searchType) {
      case 'company_name': return 'nome da empresa';
      case 'company_id': return 'ID da empresa';
      case 'mrr_value': return 'valor bruto (MRR)';
      default: return 'buscar';
    }
  };

  const toggleActionMenu = (subscriptionId: number) => {
    setOpenActionMenu(openActionMenu === subscriptionId ? null : subscriptionId);
  };

  const handleViewDetails = (subscriptionId: number, companyName: string) => {
    setSelectedSubscriptionId(subscriptionId);
    setSelectedCompanyName(companyName);
    setShowViewModal(true);
    setOpenActionMenu(null);
  };

  const handleEdit = (subscriptionId: number, companyName: string) => {
    setSelectedSubscriptionId(subscriptionId);
    setSelectedCompanyName(companyName);
    setShowEditModal(true);
    setOpenActionMenu(null);
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error('Motivo é obrigatório');
      return;
    }
    try {
      await api.put(`/companies/subscriptions/${selectedSubscriptionId}/cancel`, { reason: cancelReason });
      toast.success('Assinatura cancelada com sucesso');
      setShowCancelModal(false);
      setCancelReason('');
      setSelectedSubscriptionId(null);
      fetchSubscriptions();
    } catch (error) {
      toast.error('Erro ao cancelar assinatura');
    }
  };

  const handleDeactivate = async () => {
    try {
      await api.put(`/companies/subscriptions/${selectedSubscriptionId}/deactivate`);
      toast.success('Assinatura desativada com sucesso');
      setShowDeactivateModal(false);
      setSelectedSubscriptionId(null);
      fetchSubscriptions();
    } catch (error) {
      toast.error('Erro ao desativar assinatura');
    }
  };

  return (
    <div style={styles.container} onClick={() => setOpenActionMenu(null)}>
      <div style={styles.header}>
        <h1 style={styles.title}>Assinaturas</h1>
      </div>

      <div style={styles.scopes}>
        <button 
          onClick={() => handleFilterChange('all')} 
          style={{...styles.scopeBtn, ...(filter === 'all' ? styles.scopeBtnActive : {})}}
        >
          Todas
        </button>
        <button 
          onClick={() => handleFilterChange('subscriptions_active')} 
          style={{...styles.scopeBtn, ...(filter === 'subscriptions_active' ? styles.scopeBtnActive : {})}}
        >
          Subscriptions Ativas
        </button>
        <button 
          onClick={() => handleFilterChange('subscriptions_inactive')} 
          style={{...styles.scopeBtn, ...(filter === 'subscriptions_inactive' ? styles.scopeBtnActive : {})}}
        >
          Subscriptions Inativas
        </button>
        <button 
          onClick={() => handleFilterChange('trials')} 
          style={{...styles.scopeBtn, ...(filter === 'trials' ? styles.scopeBtnActive : {})}}
        >
          Trials
        </button>
        <button 
          onClick={() => handleFilterChange('trials_active')} 
          style={{...styles.scopeBtn, ...(filter === 'trials_active' ? styles.scopeBtnActive : {})}}
        >
          Trials Ativos
        </button>
        <button 
          onClick={() => handleFilterChange('trials_inactive')} 
          style={{...styles.scopeBtn, ...(filter === 'trials_inactive' ? styles.scopeBtnActive : {})}}
        >
          Trials Inativos
        </button>
      </div>

      <form onSubmit={handleSearch} style={styles.filters}>
        <select
          value={searchType}
          onChange={(e) => handleSearchTypeChange(e.target.value)}
          style={styles.select}
        >
          <option value="company_name">Nome da Empresa</option>
          <option value="company_id">ID da Empresa</option>
          <option value="mrr_value">Valor Bruto (MRR)</option>
        </select>
        <input
          type="text"
          placeholder={`Buscar por ${getSearchPlaceholder()}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.input}
        />
        <button type="submit" style={styles.searchBtn}>
          Buscar
        </button>
      </form>

      {loading ? (
        <div style={styles.loading}>Carregando...</div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>Company</th>
                  <th style={styles.th}>Active</th>
                  <th style={styles.th}>Valor bruto (MRR)</th>
                  <th style={styles.th}>Valor final (FATURA)</th>
                  <th style={styles.th}>Meio pgto.</th>
                  <th style={styles.th}>Vencimento</th>
                  <th style={styles.th}>Activated At</th>
                  <th style={styles.th}>Deactivated At</th>
                  <th style={styles.th}>Termo de Uso</th>
                  <th style={styles.th}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((subscription) => (
                  <tr key={subscription.id} style={styles.tableRow}>
                    <td style={styles.td}>
                      {subscription.company?.name ? (
                        <>
                          <div style={{marginBottom: '6px'}}>
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/companies?companyId=${subscription.company_id}`);
                              }}
                              style={styles.link}
                            >
                              {subscription.company.name}
                            </a>
                            {subscription.company.fancy_name && (
                              <div style={styles.fancyName}>({subscription.company.fancy_name})</div>
                            )}
                          </div>
                          {(() => {
                            const status = getBillingStatus(subscription.billing);
                            return (
                              <span style={{color: status.color, fontSize: '0.85rem'}}>
                                {status.icon} {status.text}
                              </span>
                            );
                          })()}
                        </>
                      ) : (
                        <span style={styles.textLight}>ID: {subscription.company_id}</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {subscription.active ? (
                        <span style={styles.activeBadge}>Sim</span>
                      ) : (
                        <span style={styles.inactiveBadge}>Não</span>
                      )}
                      {subscription.trial && (
                        <span style={styles.trialBadge}>Trial</span>
                      )}
                    </td>
                    <td style={styles.td}>{formatCurrency(subscription.mrr_value)}</td>
                    <td style={styles.td} dangerouslySetInnerHTML={{ __html: calculateBillValue(subscription) }} />
                    <td style={styles.td}>{subscription.payment_method || '-'}</td>
                    <td style={styles.td}>{formatClosingDay(subscription)}</td>
                    <td style={styles.td}>{formatDate(subscription.activated_at)}</td>
                    <td style={styles.td}>{formatDate(subscription.deactivated_at)}</td>
                    <td style={styles.td}>
                      <div>
                        {subscription.trial_welcome_checked && (
                          <div style={styles.checkItem}>
                            <span style={styles.checkIcon}>✓</span> trial welcome
                          </div>
                        )}
                        {subscription.terms_accepted && (
                          <div style={styles.checkItem}>
                            <span style={styles.checkIcon}>✓</span> termos de uso
                          </div>
                        )}
                        {!subscription.trial_welcome_checked && !subscription.terms_accepted && '-'}
                      </div>
                    </td>
                    <td style={styles.tdActions}>
                      <div style={styles.actionMenuContainer}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(subscription.id);
                          }}
                          style={styles.actionMenuBtn}
                          title="Ações"
                        >
                          ⋮
                        </button>
                        {openActionMenu === subscription.id && (
                          <div style={styles.actionMenu}>
                            <button
                              onClick={() => handleViewDetails(subscription.id, subscription.company?.name || '')}
                              style={styles.actionMenuItem}
                            >
                              Ver Detalhes
                            </button>
                            <button
                              onClick={() => handleEdit(subscription.id, subscription.company?.name || '')}
                              style={styles.actionMenuItem}
                            >
                              Editar
                            </button>
                            {subscription.active && (
                              <>
                                <button
                                  onClick={() => {
                                    if (!subscription.deactivated_at) {
                                      setSelectedSubscriptionId(subscription.id);
                                      setShowDeactivateModal(true);
                                      setOpenActionMenu(null);
                                    }
                                  }}
                                  style={{
                                    ...styles.actionMenuItemDeactivate,
                                    ...(subscription.deactivated_at ? styles.actionMenuItemDisabled : {})
                                  }}
                                  disabled={!!subscription.deactivated_at}
                                >
                                  Desativar
                                </button>
                                {!subscription.trial && (
                                  <button
                                    onClick={() => {
                                      if (!subscription.canceled_at) {
                                        setSelectedSubscriptionId(subscription.id);
                                        setShowCancelModal(true);
                                        setOpenActionMenu(null);
                                      }
                                    }}
                                    style={{
                                      ...styles.actionMenuItemCancel,
                                      ...(subscription.canceled_at ? styles.actionMenuItemDisabled : {})
                                    }}
                                    disabled={!!subscription.canceled_at}
                                  >
                                    Cancelar
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={styles.paginationContainer}>
            <div style={styles.paginationInfo}>
              Página {pagination.page} de {pagination.totalPages} ({pagination.total} total)
            </div>
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}

      {showViewModal && selectedSubscriptionId && (
        <CompanySubscriptionViewModal
          subscriptionId={selectedSubscriptionId}
          companyName={selectedCompanyName}
          onClose={() => {
            setShowViewModal(false);
            setSelectedSubscriptionId(null);
            setSelectedCompanyName('');
          }}
        />
      )}

      {showEditModal && selectedSubscriptionId && (
        <CompanySubscriptionEditModal
          subscriptionId={selectedSubscriptionId}
          companyName={selectedCompanyName}
          onClose={() => {
            setShowEditModal(false);
            setSelectedSubscriptionId(null);
            setSelectedCompanyName('');
          }}
          onSave={() => {
            setShowEditModal(false);
            setSelectedSubscriptionId(null);
            setSelectedCompanyName('');
            fetchSubscriptions();
          }}
        />
      )}

      {showCancelModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCancelModal(false)}>
          <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.confirmTitle}>Cancelar Assinatura</h3>
            <p>Informe o motivo do cancelamento:</p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Motivo do cancelamento..."
              style={styles.textarea}
              rows={3}
            />
            <div style={styles.confirmButtons}>
              <button onClick={() => setShowCancelModal(false)} style={styles.cancelModalBtn}>
                Voltar
              </button>
              <button onClick={handleCancel} style={styles.confirmBtn}>
                Confirmar Cancelamento
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeactivateModal && (
        <div style={styles.modalOverlay} onClick={() => setShowDeactivateModal(false)}>
          <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.confirmTitle}>Desativar Assinatura</h3>
            <p>Tem certeza que deseja desativar esta assinatura?</p>
            <div style={styles.confirmButtons}>
              <button onClick={() => setShowDeactivateModal(false)} style={styles.cancelModalBtn}>
                Cancelar
              </button>
              <button onClick={handleDeactivate} style={styles.confirmBtn}>
                Confirmar Desativação
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    color: colors.primary,
    marginBottom: '2rem',
  },
  scopes: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
    flexWrap: 'wrap' as const,
  },
  scopeBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
  },
  scopeBtnActive: {
    backgroundColor: colors.primary,
    color: colors.white,
    borderColor: colors.primary,
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap' as const,
  },
  select: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '180px',
    backgroundColor: colors.white,
  },
  input: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '300px',
  },
  searchBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  tableContainer: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    overflow: 'visible' as const,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    minWidth: '1400px',
    overflow: 'visible' as const,
  },
  tableHeader: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  th: {
    padding: '1rem',
    textAlign: 'left' as const,
    fontWeight: '600',
  },
  tableRow: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: '1rem',
    color: colors.text,
  },
  tdActions: {
    padding: '1rem',
    color: colors.text,
    minWidth: '80px',
    position: 'relative' as const,
  },
  link: {
    color: colors.primary,
    textDecoration: 'none',
    cursor: 'pointer',
    fontWeight: 'bold' as const,
  },
  fancyName: {
    fontSize: '0.9rem',
    color: colors.textLight,
    marginTop: '0.25rem',
  },
  activeBadge: {
    backgroundColor: colors.success,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    marginRight: '0.5rem',
  },
  inactiveBadge: {
    backgroundColor: colors.danger,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    marginRight: '0.5rem',
  },
  trialBadge: {
    backgroundColor: colors.warning,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  termsBadge: {
    backgroundColor: colors.primary,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  actionMenuContainer: {
    position: 'relative' as const,
  },
  actionMenuBtn: {
    padding: '8px 12px',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  actionMenu: {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1001,
    minWidth: '140px',
  },
  actionMenuItem: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: 'inherit',
  },
  actionMenuItemDeactivate: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.warning,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: 'inherit',
  },
  actionMenuItemCancel: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.danger,
    fontFamily: 'inherit',
  },
  actionMenuItemDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  textLight: {
    fontSize: '0.9rem',
    color: colors.textLight,
  },
  checkItem: {
    fontSize: '0.85rem',
    marginBottom: '0.25rem',
  },
  checkIcon: {
    color: colors.success,
    fontWeight: 'bold' as const,
    marginRight: '0.25rem',
  },
  paginationContainer: {
    marginTop: '2rem',
  },
  paginationInfo: {
    textAlign: 'center' as const,
    color: colors.textLight,
    marginBottom: '1rem',
  },
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1002,
  },
  confirmModal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    padding: '24px',
    width: '400px',
    maxWidth: '90vw',
  },
  confirmTitle: {
    margin: '0 0 16px 0',
    color: colors.primary,
  },
  textarea: {
    width: '100%',
    padding: '8px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical' as const,
    marginBottom: '16px',
  },
  confirmButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  cancelModalBtn: {
    padding: '8px 16px',
    backgroundColor: colors.border,
    color: colors.text,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  confirmBtn: {
    padding: '8px 16px',
    backgroundColor: colors.danger,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
};

export default CompanySubscriptions;
